import React from 'react';
import { Link } from 'src/components/Inputs';
import AvailableLanguageLink from '../AvailableLanguageLink';

const EnPlaidAccountTerms = ({ availableLanguages }) => {
  return (
    <>
      <h4 id='plaid-account-terms' data-magellan-target='plaid-account-terms'>
        Plaid Account Terms
      </h4>

      <p>Available languages:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />
      <p>Effective Date: September 28, 2026</p>
      <p>
        <strong>Background</strong>. Thanks for using Plaid! Plaid helps
        consumers like you connect your financial accounts to Plaid, enabling
        Plaid to provide you with services that you request, and to third-party
        applications that use Plaid (“<strong>Third-Party Apps</strong>”). You
        can save your financial account and identity information in an account
        directly with Plaid (“<strong>Plaid Account</strong>”). Your Plaid
        Account unlocks Plaid’s consumer services, as described further below.
      </p>
      <p>
        <strong>Additional Terms.</strong> These Plaid Account terms (“
        <strong>Plaid Account Terms</strong>”) supplement and incorporate by
        reference the{' '}
        <Link href='#end-user-services-agreement-us'>
          <a>End User Services Agreement (US)</a>
        </Link>
        , and together form an agreement between you and Plaid (the “
        <strong>Agreement</strong>”).{' '}
        <strong>
          <em>
            Please review all terms in the{' '}
            <Link href='#end-user-services-agreement-us'>
              <a>End User Services Agreement (US)</a>
            </Link>
            , including in particular the Sections titled Rights to the
            Platform, Our Disclaimers, Liabilities for our Platform, Dispute
            Resolution, and Notices. Plaid may modify the Agreement upon notice
            to you, as detailed in the last paragraph of the{' '}
            <Link href='#end-user-services-agreement-us'>
              <a>End User Services Agreement (US)</a>
            </Link>
            , and acceptance of these modifications will be required to continue
            using your Plaid Account.
          </em>
        </strong>
      </p>
      <p>
        <strong>General</strong>. Plaid allows you to save your financial
        account and identity information in an account with Plaid (“
        <strong>Plaid Account</strong>”). This enables Plaid to provide you with
        a range of services, including allowing you to more easily access and
        manage your financial data and account connections, enable streamlined
        access to and onboarding onto Third-Party Apps, enable faster identity
        verification, receive bank account monitoring and security alerts, and
        create cashflow-based insights to enable broader access to credit and
        other services. Note that a Plaid Account is not a transaction or asset
        account for purposes of the Electronic Fund Transfer Act, does not store
        funds, and does not provide you with a means to initiate electronic fund
        transfers or other payments, but instead enables the Plaid Account
        services described in these terms.
      </p>
      <p>
        <strong>Streamlined Access and Onboarding</strong>. Your Plaid Account
        allows you to provide your identity information and share your financial
        data with Third-Party Apps and Plaid more quickly and easily. This helps
        us accurately recognize you when you return to another Third-Party App
        and help you complete faster identity verification and financial account
        linking without you having to re-enter all of the information, allowing
        you to access and onboard onto Third-Party Apps in a streamlined
        fashion.
      </p>
      <p>
        <strong>Bank Account Monitoring.</strong>
      </p>
      <p>
        Plaid also offers bank account monitoring and alerts as part of your
        Plaid Account.
      </p>
      <ul>
        <li>
          <p>
            <strong>Alerts.</strong> You can set up your Plaid Account to
            monitor the financial accounts saved in your Plaid Account and to
            send you notifications and alerts related to your accounts,
            including regarding: suspicious or unusual activity, unusual
            transactions, identity mismatches among saved accounts, and the
            status of your financial account connections to Third-Party Apps.
            During Plaid’s account linking process, you can enroll in these
            monitoring and alert services. If you choose to enroll, you agree to
            receive such notifications and alerts from Plaid, including via
            email. If eligible, you may choose to also set up monitoring and
            alerting services through Plaid’s web application (“
            <strong>Plaid Web-App</strong>”), available at account.plaid.com.
          </p>
        </li>
        <li>
          <p>
            <strong>Monitoring Limitations.</strong> While Plaid implements
            measures designed to detect suspicious or unusual account activity
            such as unusual transactions or connections to Third-Party Apps, no
            monitoring service can guarantee detection of all such activity. You
            remain responsible for regularly reviewing your financial account
            statements and identity information and promptly reporting any
            unauthorized transactions or activity to your financial institution
            or other applicable entity. Plaid’s services supplement, but do not
            replace, your own review and the security measures provided by your
            financial institutions and other entities providing you their
            service.
          </p>
        </li>
      </ul>
      <p>
        <strong>Plaid CRA Consumer Reporting Services.</strong> Your Plaid
        Account also offers services that allow you to share consumer reports
        about your credit and financial information (including transaction
        history, account balances, and identity data) with companies that have a
        permissible purpose under the FCRA to receive them (“Consumer Report
        Users”). These reports may include credit scores, income insights, and
        other cash flow attributes, and may be used to support a range of
        eligibility decisions about you, including for credit, housing, and
        other purposes permitted under applicable law. These services are
        provided by Plaid Consumer Reporting Agency, Inc. (dba Plaid Check)
        (“Plaid CRA”), a consumer reporting agency and a subsidiary of Plaid
        Inc., and are governed by additional terms set forth in Plaid CRA’s{' '}
        <Link href='https://plaid.com/plaid-check-consumer-report/consumer-services-agreement/'>
          <a>Consumer Services Agreement</a>
        </Link>{' '}
        and{' '}
        <Link href='https://plaid.com/plaid-check-consumer-report/privacy-policy/'>
          <a>Privacy Policy</a>
        </Link>
        .
      </p>
      <p>
        <strong>How Your Plaid Account Works</strong>. You can choose to create
        a Plaid Account when you use Plaid in connection with Third-Party Apps
        or on the Plaid Web-App. By creating a Plaid Account, you authorize
        Plaid to collect, retain, and use your information, including identity
        and financial account information, in accordance with the Agreement and
        Plaid’s{' '}
        <Link href='#end-user-privacy-policy'>
          <a>End User Privacy Policy</a>
        </Link>
        , including to provide you with the Plaid Account services described in
        these Plaid Account Terms. Depending on the specific service provided by
        your Plaid Account, this information may include account and routing
        numbers, identity and contact information, transaction history, account
        balances, device and IP information, documentary identification, and
        photos, images, or scans of your face for identity verification.
      </p>
      <p>
        As part of enabling Plaid Account services for you, Plaid may: (a)
        directly receive and manage your financial account and identity
        information from your financial institution for the financial accounts
        you save in your Plaid Account; (b) share with Third-Party Apps that you
        use, our subsidiaries, and our service providers any information
        required to provide the product or service you have requested; (c) share
        with Third-Party Apps that you use, our subsidiaries, and our service
        providers information about whether or not you have a Plaid Account,
        and, if you do, certain information from your Plaid Account in order for
        us and them to provide services to you, including, but not limited to:
        (i) the number and types of financial accounts and Third-Party App
        connections saved in your Plaid Account, (ii) types of saved identity
        data and documents in your Plaid Account, and (iii) the services and use
        cases that Third-Party Apps provide to you that can be supported by the
        financial accounts and identity data saved in your Plaid Account, and
        (iv) fraud risk and security indicators derived from your Plaid Account
        data; (d) contact you using information that you provide or that is
        available in your saved identity data or financial accounts, including
        to send security notifications, monitoring alerts, and other
        communications related to your Plaid Account services; (e) verify your
        data to protect against or prevent actual or potential fraud,
        misrepresentation, unauthorized or suspicious activity, claims, or other
        liability, or other malicious behavior – data we receive from this
        verification process, including data that is potentially considered
        biometric data in your jurisdiction, may be stored in your Plaid
        Account; and (f) use the financial account and identity information in
        your Plaid Account, including any information received from such
        verification process, to provide Plaid Account services, including to
        enable streamlined onboarding when you return to share identity data
        with other Third-Party Apps or when otherwise providing Plaid Account
        services such as bank account monitoring.
      </p>
      <p>
        For more information about Plaid’s collection, use, and sharing of
        personal information about you, including in connection with your Plaid
        Account, please review Plaid’s{' '}
        <Link href='#end-user-privacy-policy'>
          <a>End User Privacy Policy</a>
        </Link>
        . The Agreement and Plaid’s{' '}
        <Link href='#end-user-privacy-policy'>
          <a>End User Privacy Policy</a>
        </Link>{' '}
        do not cover what the Third-Party Apps that you use do with your
        information. You should review the privacy policies and terms of service
        for those Third-Party Apps for information about their practices.
      </p>
      <p>
        <strong>Managing and Deleting Your Plaid Account</strong>. You can view
        and manage your Plaid Account using the Plaid Web-App, using Plaid
        Portal at{' '}
        <Link href='https://my.plaid.com/'>
          <a>my.plaid.com</a>
        </Link>
        , or by contacting{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        . If you would like to delete your Plaid Account or have any questions
        about managing your Plaid Account or regarding your data rights, please
        contact{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        . For more information about Plaid’s retention and deletion practices,
        please review Plaid’s{' '}
        <Link href='#end-user-privacy-policy'>
          <a>End User Privacy Policy</a>
        </Link>
        .
      </p>
      <p>
        <strong>Your Use and Restrictions</strong>. You may only use your Plaid
        Account and the Plaid Web-App to receive services from Plaid, including
        receiving bank account monitoring alerts, enabling streamlined access to
        and onboarding onto your Third-Party Apps, and receiving credit-related
        insights and services. By creating or using a Plaid Account, you
        expressly agree to the Agreement and that: (a) all data you provide and
        all financial accounts you connect are your own or that you are an
        authorized user of those accounts; (b) all data you provide to verify
        your identity is complete and accurate; and (c) you will not (i) use or
        access anyone else’s Plaid Account or related data, (ii) use any Plaid
        Account or related data for any fraudulent, illegal or misleading
        purpose, (iii) modify, reverse engineer or seek to gain unauthorized
        access to any Plaid Account or related Plaid systems, (iv) bypass or
        circumvent measures designed to prevent or limit access to any part of
        any Plaid Account or related Plaid systems, (v) copy, modify or create
        derivative works of any Plaid Account or related Plaid systems in any
        manner not authorized by Plaid, (vi) access any Plaid Account or related
        Plaid systems for competitive purposes or publish any benchmark or
        performance information, or (vii) use any Plaid Account or related Plaid
        systems in any manner that could damage, disable, overburden, or impair
        the functioning thereof or interfere with, disrupt, or negatively affect
        other users.
      </p>
      <p>
        You acknowledge and understand that you are responsible for the data
        that you save in your Plaid Account. You will not provide any data that
        is: (i) defamatory, abusive, harassing, threatening, or an invasion of a
        right or the privacy of another person; (ii) bigoted, hateful, or
        racially or otherwise offensive; (iii) violent, vulgar, obscene,
        pornographic, or otherwise sexually explicit; (iv) trademarked or
        copyrighted content, or content that otherwise infringes on the
        intellectual property rights of another individual or company; or (v)
        otherwise harmful or can be reasonably expected to be harmful to any
        person or entity. Any unauthorized use of a Plaid Account is strictly
        prohibited and may result in deletion of your Plaid Account.
      </p>
      <p>
        <strong>Wireless Device and SMS Terms.</strong> You agree to notify us
        promptly if you change or relinquish your phone number, as your phone
        number helps verify your identity and protect your Plaid Account from
        unauthorized access. Plaid may send you Short Message Service (SMS)
        messages in order to provide a service to you and/or to confirm your
        identity by way of two-factor authentication. You consent to receive
        automated SMS messages from Plaid, and any of our SMS-related service
        providers, including through the use of an automatic telephone dialing
        system, at the telephone number you have provided. Message and data
        rates may apply. As a general matter, you will receive one message per
        request. Carriers are not liable for delayed or undelivered messages.
      </p>
      <p>
        You authorize your wireless carrier to use or disclose information about
        your account and your wireless device, if available, to Plaid or our
        service provider for the duration of your business relationship, solely
        to help Plaid or our service provider identify you or your wireless
        device and to prevent fraud. Please see our{' '}
        <Link href='#end-user-privacy-policy'>
          <a>End User Privacy Policy</a>
        </Link>{' '}
        to see how we treat your data.
      </p>
      <p>
        The information your wireless carrier may disclose may include mobile
        number, name, address, email, network status, customer type, customer
        role, billing type, and mobile device identifiers (IMSI and IMEI). No
        mobile information will be shared with third parties or affiliates for
        marketing or promotional purposes. Text messaging originator opt-in data
        and consent information will not be shared with any third parties.
      </p>
      <p>
        If you do not wish to receive SMS messages from Plaid or our service
        providers, or if you no longer want your wireless operator to share
        information about you with Plaid or our service providers, you agree to
        reply STOP to any SMS message from Plaid or sent on behalf of Plaid in
        order to opt out of the SMS message notifications. You may receive an
        additional SMS message confirming your decision to opt out. After opting
        out of SMS messages, you will no longer receive messages from Plaid or
        our service providers unless you subsequently request SMS messages from
        us or sign up for any of our services, such as a Plaid Account, that
        require sending you SMS messages.
      </p>
      <p>
        If you are experiencing issues with the SMS messages from Plaid or our
        service providers, you can reply with the keyword HELP for more
        assistance, or you can contact{' '}
        <Link href='mailto:login+sms@plaid.com'>
          <a>login+sms@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        <strong>Email Notifications.</strong> In addition to SMS messages, Plaid
        may send you email notifications as part of your Plaid Account services,
        including bank account monitoring alerts, security notifications, and
        other communications related to your Plaid Account. You consent to
        receive such email notifications at the email address associated with
        your Plaid Account. You may manage your email notification preferences
        by contacting{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        , or if eligible and enrolled, through the Plaid Web-App. If you do not
        wish to receive monitoring alert emails from Plaid, you may opt out of
        such emails; however, opting out of monitoring alert emails does not
        deactivate your Plaid Account or other Plaid Account services.
      </p>
      <p>
        <strong>
          Persons under the age of 18 may not create a Plaid Account
        </strong>
        . By creating and using your Plaid Account or using the Plaid Web-App,
        you represent and warrant that you are 18 years old or older. If you
        have reason to believe that a person under 18 years of age is using our
        Services, please contact us at{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>{' '}
        and, upon confirmation, we will delete the relevant Plaid Account,
        subject to and in compliance with our requirements under applicable law.
      </p>
      <p>
        <strong>Security</strong>. Plaid implements technical and organizational
        measures designed to safeguard your Plaid Account from unauthorized
        access, use, alteration, or disclosure and to maintain the security,
        confidentiality, and integrity of your data. However, we encourage you
        to take responsibility for securing your Plaid Account and other
        personal information, knowing that you provide it at your own risk. You
        should also be aware that while Plaid’s monitoring services are designed
        to help detect suspicious activity, they do not replace the security
        measures provided by your financial institutions and Third-Party Apps,
        and you should continue to follow their recommendations where applicable
        for the security, confidentiality, and integrity of your data and
        accounts. If you believe your Plaid Account has been compromised, please
        contact us immediately at{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        <strong>Miscellaneous</strong>. Any capitalized terms used but not
        defined in these Plaid Account Terms will have the meanings ascribed to
        them in the{' '}
        <Link href='#end-user-services-agreement-us'>
          <a>End User Services Agreement (US)</a>
        </Link>
        . In the event of a conflict between these Plaid Account Terms and the{' '}
        <Link href='#end-user-services-agreement-us'>
          <a>End User Services Agreement (US)</a>
        </Link>
        , these Plaid Account Terms will control and prevail as applicable to
        your use of a Plaid Account; provided that all other provisions of the{' '}
        <Link href='#end-user-services-agreement-us'>
          <a>End User Services Agreement (US)</a>
        </Link>{' '}
        will remain in full force and effect. By accepting the Agreement,
        including these Plaid Account Terms, or by creating, accessing,
        managing, or using your Plaid Account, you agree to the Agreement and
        certify that you have all the necessary rights to do so. If you do not
        agree to the Agreement, please do not create, access, manage, or use a
        Plaid Account. If, at any time, you no longer agree to the Agreement,
        you may delete your Plaid Account. Plaid, in our sole discretion, may
        disable or delete a Plaid Account if (a) we suspect any violation of any
        of the terms contained in the Agreement, (b) your Plaid Account is
        unused and remains inactive for an extended period of time, (c) we
        suspect any unauthorized use of your Plaid Account, or (d) we are
        legally obligated to do so. Plaid reserves the right to seek all other
        available remedies, in law or in equity, in connection with any such
        issue.
      </p>

      <hr className='section-divider' />
    </>
  );
};

export default EnPlaidAccountTerms;
